import { Button, Col, Dropdown, Form, Offcanvas, Row } from "react-bootstrap"
import { useEffect, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { DeleteRequest, GetRequest, PostRequest } from "../../Utils/hooks/ApiHandler"
import useLocalStorageState from "use-local-storage-state"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { customStyles, formatDate, getValueOrDash } from "../../Utils/helpers/commonhelper"
import { FaEllipsisV, FaTrash } from "react-icons/fa";
import DeleteModal from "../DeleteModal"
import Select from "react-select"
import { baseURL } from "../../Utils/constants/constant"
import { ContactDetails } from "../detailPage/Contacts/ContactDetails"
import { DealDetails } from "../deals/DealDetails"
import { LeadsDetails } from "../detailPage/leads/LeadsDetails"
import { CompanyDetails } from "../detailPage/CompanyDetails"
import useCommonData from "../../Utils/actions/advanceFilteringAction"

// Zod Schema for Validation
const followUpSchema = z.object({
  nextFollowUpDate: z.string().min(1, "Next Follow up date is required"),
  time: z.string().min(1, "Time is required"),
  feedback: z.string().min(1, "Feedback is required"),
  priority: z.string().min(1, "Priority is required"),
})

export const FollowUpPage = ({
  id,
  // onFollowUpUpdated,
  module_name,
  followupGrant
}) => {
  // Main component state
  const [followUpData, setFollowUpData] = useState(null)
  const [feedbackHistory, setFeedBackHistory] = useState([])
  const [customer] = useLocalStorageState("customer", { token: {} })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [feedbackToDelete, setFeedbackToDelete] = useState(null)
  // delete modal loading state
  const [loading, setLoading] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const [isOverdue, setIsOverdue] = useState(false)
  // Auth and user data
  const loginUserId = customer?.customer_details?.id || ""
  const token = customer?.token?.authorization || ""
  const rf = customer?.token?.authentication || ""
  const headers = { Authorization: token, Authentication: rf }
  const navigate = useNavigate()
  const extras = { customer, navigate }

  // Form handling
  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(followUpSchema),
    defaultValues: {
      nextFollowUpDate: "",
      time: "",
      feedback: "",
      priority: "low",
    },
  })

  const priorityOpt = [
    {
      id: 'low', label: "Low"
    },
    {
      id: 'medium', label: 'Medium'
    },
    {
      id: 'high', label: "High"
    }
  ]

  // Formats time from 24-hour to 12-hour format
  const formatTime = (timeStr) => {
    if (!timeStr) return ""
    let [hour, minute] = timeStr.split(":")
    hour = Number.parseInt(hour)
    const ampm = hour >= 12 ? "PM" : "AM"
    hour = hour % 12 || 12
    return `${hour}:${minute} ${ampm}`
  }


  // state for opening contact details offcanvas
  const [showContactDetails, setShowContactDetails] = useState(false)
  const [contactId, setContactId] = useState(null)

  // state for storing lead or deal id respectively
  const [selectedDealId, setSelectedDealId] = useState(null);
  const [selectedLeadId, setSelectedLeadId] = useState(null);

  // states for opening lead and deal details offcanvas respectively
  const [showDealDetails, setShowDealDetails] = useState(false);
  const [showLeadDetails, setShowLeadDetails] = useState(false);

  // company states
  const [companyId, setCompanyId] = useState(null)
  const [showCompanyDetails, setShowCompanyDetails] = useState(false)


  // common advance filter api calls that will be needed everywhere
  const { company, contacts } = useCommonData({
    leadOwner: false,
    leadSource: false,
    companies: true,
    contacts: true
  })

  // Fetches follow-up data for the current lead
  const fetchFollowUp = async () => {
    if (!id) return
    setIsFetching(true)
    try {
      let res;
      if (module_name === "lead") {
        res = await GetRequest(`${module_name}/fetchLeadFollowup/${id}`, { headers }, extras)
      } else {
        res = await GetRequest(`${module_name}s/fetchDealFollowup/${id}`, { headers }, extras)
      }
      if (res.data) {
        const data = res.data
        setFollowUpData(data)
        setFeedBackHistory(res.data.feedback)


        // Split datetime into date and time parts for next follow up
        const [datePart, timePartFull] = data.next_followup?.split(" ") || ["", ""]
        const timePart = timePartFull?.slice(0, 5) // 'HH:MM'

        // New logic for setting followup date and time
        const today = new Date()
        const todayStr = today.toISOString().split("T")[0]

        const overdue = data.followup_date && data.followup_date < todayStr
        setIsOverdue(!!overdue)

        const followupDate = data.followup_date || datePart || ""
        const followupTime = data.followup_time || timePart || ""

        let finalDate = ""
        let finalTime = ""

        if (followupDate && followupDate > todayStr) {
          finalDate = followupDate
          finalTime = followupTime || today.toTimeString().slice(0, 5)
        } else {
          const nextDay = new Date()
          nextDay.setDate(today.getDate() + 1)
          finalDate = nextDay.toISOString().split("T")[0]
          finalTime = today.toTimeString().slice(0, 5)
        }

        // Reset form with proper handling of feedback array
        reset({
          nextFollowUpDate: finalDate,
          time: finalTime,
          feedback: "",
          priority: "low",
        })
      }
    } catch (err) {
      toast.error("Failed to fetch follow-up data")
    } finally {
      setTimeout(() => {
        setIsFetching(false)
        return
      }, 200);
    }
  }

  // Handles form submission for new follow-up
  const onSubmit = async (formData) => {
    if (!followupGrant.isCreate) {
      toast.warning("You are not authorized for this action!")
      return
    }
    try {
      const leadPayload = {
        user_login_id: loginUserId,
        lead_id: id,
        followup_time: formData.time,
        followup_date: formData.nextFollowUpDate,
        feedback: formData.feedback,
        priority: formData.priority,
      }
      const dealPayload = {
        user_login_id: loginUserId,
        deal_id: id,
        followup_time: formData.time,
        followup_date: formData.nextFollowUpDate,
        feedback: formData.feedback,
        priority: formData.priority,
      }
      let res;
      if (module_name === "lead") {
        res = await PostRequest(`/lead/takeLeadFollowup`, leadPayload, null, { headers }, extras)
      } else {
        res = await PostRequest(`/deals/takeDealFollowup`, dealPayload, null, { headers }, extras)
      }
      if (res.status === "success") {
        toast.success(res.message)
        fetchFollowUp()
        // onFollowUpUpdated?.()
        reset()
      } else {
        toast.error(res.message)
      }
    } catch (err) {
      toast.error("Error updating follow up")
    }
  }

  // Show delete confirmation modal
  const handleDeleteFeedback = (feedback_id) => {
    setFeedbackToDelete(feedback_id)
    setShowDeleteModal(true)
  }

  // Handle confirmed deletion
  const handleDeleteConfirm = async () => {
    setLoading(true)
    try {
      let res;
      if (module_name === "lead") {
        res = await DeleteRequest(
          `lead/deleteLeadsFollowup/${feedbackToDelete}/${loginUserId}`,
          { headers },
          extras
        )
      } else {
        res = await DeleteRequest(
          `deals/deleteLeadsFollowup/${feedbackToDelete}/${loginUserId}`,
          { headers },
          extras
        )
      }

      if (res.status === "success") {
        toast.success("Feedback deleted successfully")
        setLoading(false)
        fetchFollowUp() // Refresh the data
      } else {
        setLoading(false)
        toast.error(res.message || "Failed to delete feedback")
      }
    } catch (err) {
      toast.error("Error deleting feedback")
    } finally {
      setLoading(false)
      setShowDeleteModal(false)
      setFeedbackToDelete(null)
    }
  }

  // Handles modal close and resets form
  const handleClose = () => {
    reset()
    setIsOverdue(false)
  }

  // Fetch follow-up data when component mounts or leadId changes
  useEffect(() => {
    if (id) {
      fetchFollowUp()
    }
  }, [id])

  if (!followUpData || isFetching) {
    return <FollowupDetailSkeleton />
  }

  return (
    <Col md={7} className="px-4  Projectscard border-0" style={{ minHeight: "calc(100vh - 14rem)", maxHeight: "calc(100vh - 14rem)", overflowY: "auto", overflowX: "hidden", position: 'relative' }}>
      <style>
        {`
          .w-75::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          .w-75::-webkit-scrollbar-track {
            background: transparent;
          }
          .w-75::-webkit-scrollbar-thumb {
            background-color: rgb(122, 58, 94);
            border-radius: 4px;
          }
          .w-75::-webkit-scrollbar-thumb:hover {
            background-color: rgb(102, 48, 79);
          }
          .slide-in {
            transform: translateY(10px);
            opacity: 0;
            animation: slideInAnim 0.8s ease-out forwards;
          }

          @keyframes slideInAnim {
            from {
              transform: translateY(10px);
              opacity: 0;
            }
            to {
              transform: translateY(0px);
              opacity: 1;
            }
          }
        `}
      </style>

      <DeleteModal
        show={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setFeedbackToDelete(null)
        }}
        onConfirm={handleDeleteConfirm}
        isLoading={loading}
        message="Are you sure you want to delete this feedback?"
      />

      <div className="card-header d-flex flex-column align-items-start justify-content-between  mb-2 pb-0 w-100 position-relative">
        <h3 style={{ textTransform: "capitalize", fontWeight: 400 }}>
          Followup {module_name}s
        </h3>
        <span className="text-muted" style={{ fontSize: "12px" }}>
          Modify Followup details, Mediatory fields must required.
        </span>
        {isOverdue && (
          <div
            style={{
              position: "absolute",
              top: "0px",
              right: "0px",
              background: "#dc3545",
              color: "#fff",
              padding: "6px 12px",
              borderRadius: "14px",
              fontSize: "12px",
              fontWeight: 600,
              letterSpacing: "0.5px",
            }}
          >
            OVERDUED {formatDate(followUpData.followup_date) || "-"}
          </div>
        )}
      </div>

      <hr style={{ margin: "0px 0px ", }} className="w-100" />

      <div className="slide-in">
        {/* Lead Information Section */}
        <div className="d-flex pt-4 flex-column w-100">
          <h6 className=" mb-3" style={{ fontWeight: 300 }}>Personal Details</h6>

          <div
            className="w-100"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              gap: "1rem 2rem",
            }}
          >
            {console.log("followUpData", followUpData)}
            <InfoRow handler={() => {
              setContactId(followUpData?.contact_id)
              setShowContactDetails(true)
            }}
              label="Contact Name" value={followUpData?.contact_name} />
            <InfoRow handler={() => {
              setCompanyId(followUpData?.company_id)
              setShowCompanyDetails(true)
            }} label="Company Name" value={followUpData?.company_name} />
            <InfoRow handler={() => {
              if (followUpData?.prefix === null) return
              if (module_name === "lead") {
                setSelectedLeadId(id)
                setShowLeadDetails(true)
                return
              }
              setSelectedDealId(id)
              setShowDealDetails(true)
            }} label={module_name === "lead" ? "Lead ID" : "Deal ID"} value={followUpData?.prefix} />
            <InfoRow label="Enquiry Source" value={followUpData?.source_title} />
            <InfoRow label="Contact Email" value={followUpData?.contact_email} />

            <InfoRow label="Lead Owner" value={followUpData?.owner_name} />
            <InfoRow label="Contact Phone" value={`+${JSON.parse(followUpData?.user_phone)[0].country_code} ${followUpData?.contact_phone}`} />
            <InfoRow label="Company Phone" value={followUpData?.company_phone} />
          </div>
        </div>

        {/* Feedback History Section */}
        <h3 className="mt-4 mb-0" style={{ fontSize: "1rem", fontWeight: 400 }}>
          Feedback History
        </h3>
        <hr className="my-2" />

        <div className="mb-4">
          {
            followupGrant.isView ?
              feedbackHistory?.length > 0 ? (
                feedbackHistory.map((item, index) => {
                  const [date, time] = item.followed_on?.split(" ") || ["", ""]
                  return (
                    <div key={index} style={{ marginTop: '1.6rem' }} className=" border-start border-3 ps-3 border-danger">
                      <div style={{ position: "relative" }}>
                        <p className="mb-1 text-muted">
                          <strong className="text-pink">@{item.followed_by}</strong> called on{" "}
                          <span className="" >{formatDate(date)}</span> at{" "}
                          <span className="text-black " style={{ fontWeight: 300 }}>{formatTime(time)}</span>

                          <span
                            className="px-2 py-1"
                            style={{
                              fontSize: "10px",
                              marginLeft: "0.5rem",
                              borderRadius: "10px",
                              background:
                                item.priority === "high"
                                  ? "#FBE9E7"
                                  : item.priority === "medium"
                                    ? "#E8EAF6"
                                    : "#E8F4FD",
                              color:
                                item.priority === "high"
                                  ? "#D84315"
                                  : item.priority === "medium"
                                    ? "#3F51B5"
                                    : "#0B5ED7",
                              fontWeight: 500,
                              textTransform: "capitalize",
                            }}
                          >
                            {getValueOrDash(item.priority)}
                          </span>
                        </p>


                        <Dropdown
                          align="end"
                          style={{
                            position: "absolute",
                            top: "0.1rem",
                            right: "0rem",
                          }}
                        >
                          <Dropdown.Toggle
                            as="div"
                            style={{
                              cursor: "pointer",
                              color: "#6c757d",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "24px",
                              height: "24px",
                            }}
                            className="no-arrow"
                          >
                            <FaEllipsisV size={14} />
                          </Dropdown.Toggle>

                          <Dropdown.Menu>
                            <Dropdown.Item
                              className="text-danger d-flex align-items-center gap-2"
                              onClick={() => {
                                if (!followupGrant.isDelete) {
                                  toast.warning("You are not authorized for this action!")
                                  return
                                }
                                handleDeleteFeedback(item.feedback_id)
                              }}
                            >
                              <FaTrash size={12} />
                              Delete
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                      <p className="mb-0 " style={{ color: "#a8abad", fontWeight: 300 }}>{getValueOrDash(item.feedback)}</p>
                    </div>
                  )
                })
              ) : (
                <p className="text-muted">No feedback history available</p>
              )
              :
              <>You are not authorized to view followup!</>
          }
        </div>

        <div>
          <h3 className="mt-4 mb-0" style={{ fontSize: "1rem", fontWeight: 400 }}>Feedback Form</h3>
          <hr className="my-2" />
          <Form onSubmit={handleSubmit(onSubmit)} className="form-container">
            <Row className="mb-3 ">
              <Col md={4}>
                <Form.Group controlId="nextFollowUpDate" className="focus-z-index position-relative">
                  <Form.Label>Next Follow up Date <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    {...register("nextFollowUpDate")}
                    isInvalid={!!errors.nextFollowUpDate}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.nextFollowUpDate?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group controlId="time" className="position-relative focus-z-index">
                  <Form.Label className="w-auto fw-lighter">Time <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="time"
                    {...register("time")}
                    isInvalid={!!errors.time}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.time?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="focus-z-index position-relative" controlId="priority">
                  <Form.Label>Priority</Form.Label>
                  <Controller
                    name={"priority"}
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        className="w-100"
                        options={priorityOpt.map((p) => ({ value: p.id, label: p.label }))}
                        value={
                          watch("priority")
                            ? {
                              value: watch("priority"),
                              label:
                                priorityOpt.find((c) => c.id === watch("priority"))?.label,
                            }
                            : { id: "low", label: "Low" }
                        }
                        onChange={(selectedOption) => {
                          const newValue = selectedOption ? selectedOption.value : ""
                          setValue("priority", newValue)
                        }}
                        isClearable={true}
                        styles={customStyles}
                        classNamePrefix="react-select"
                        placeholder="Select Priority"
                      />
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.priority?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={12} className="mt-2">
                <Form.Group className="focus-z-index position-relative" controlId="feedback">
                  <Form.Label>Feedback <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Enter your Feedback"
                    {...register("feedback")}
                    isInvalid={!!errors.feedback}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.feedback?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end mt-4 gap-2">
              <Button className="btn btn-default-outline" variant="custom-variant" onClick={handleClose}>
                Cancel
              </Button>
              <Button className="btn btn-default" type="submit">
                Submit
              </Button>
            </div>
          </Form>
        </div>
      </div>

      {
        showContactDetails && contactId !== null &&
        <ContactDetails
          show={showContactDetails}
          setShowContactDetailsCanvas={setShowContactDetails}
          onlyDetail={true}
          id={contactId}
          allContacts={contacts}
          onCanvasClose={() => {
            setShowContactDetails(false)
          }}
        />
      }
      {
        showDealDetails && selectedDealId !== null && followUpData?.prefix !== null && (
          <DealDetails
            show={showDealDetails}
            onClose={() => setShowDealDetails(false)}
            dealId={selectedDealId}
          />
        )
      }
      {
        showLeadDetails && selectedLeadId !== null && followUpData?.prefix !== null && (
          <LeadsDetails
            show={showLeadDetails}
            onClose={() => setShowLeadDetails(false)}
            leadId={selectedLeadId}
          />
        )
      }

      {showCompanyDetails && companyId !== null &&
        <CompanyDetails
          show={showCompanyDetails}
          onCanvasClose={() => setShowCompanyDetails(false)}
          id={companyId}
          allCompanies={company}
          setShowCompanyDetails={setShowCompanyDetails}
          onlyDetails={true}
        />
      }
    </Col >
  )
}

const InfoRow = ({ label, value, handler, }) => {
  const lowerCaseLabel = label?.toLowerCase()?.split(" ")?.join("_")
  return <div className="overflow-hidden mb-2  ">
    <span className="mb-0" style={{ ...miniLabelStyle }}>{label}</span>
    <div
      onClick={handler}
      className={`mb-0 text-truncate `}
      style={{
        color: (lowerCaseLabel === "contact_name" || lowerCaseLabel === "company_name" || (lowerCaseLabel === "lead_id" && value !== null) || (lowerCaseLabel === "deal_id" && value !== null)) && "#0099ff",
        fontSize: "14px",
        cursor: (lowerCaseLabel === "contact_name" || lowerCaseLabel === "company_name" || (lowerCaseLabel === "lead_id" && value !== null) || (lowerCaseLabel === "deal_id" && value !== null)) && "pointer",
        fontWeight: 400
      }}
    >
      {getValueOrDash(value)}
    </div>
  </div>
}

const FollowupDetailSkeleton = () => {
  return <Col md={7} className="px-4 Projectscard border-0">
    <div className="p-3">
      {/* Header Skeleton */}
      <div className="mb-3">
        <div style={{ height: "20px", width: "180px", background: "#e0e0e0", borderRadius: "4px" }}></div>
        <div style={{ height: "12px", width: "250px", background: "#f0f0f0", borderRadius: "4px", marginTop: "6px" }}></div>
      </div>

      <hr style={{ opacity: 0.1 }} />

      {/* Info Section Skeleton */}
      <div className="d-flex gap-4 py-3">
        <div style={{ width: "40%" }}>
          {[...Array(5)].map((_, i) => (
            <div key={i} style={{ height: "12px", background: "#f0f0f0", marginBottom: "8px", borderRadius: "4px" }}></div>
          ))}
        </div>
        <div style={{ width: "40%" }}>
          {[...Array(4)].map((_, i) => (
            <div key={i} style={{ height: "12px", background: "#f0f0f0", marginBottom: "8px", borderRadius: "4px" }}></div>
          ))}
        </div>
      </div>

      {/* Feedback History Skeleton */}
      <div className="mt-4">
        <div style={{ height: "16px", width: "150px", background: "#e0e0e0", borderRadius: "4px" }}></div>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="mt-3">
            <div style={{ height: "12px", width: "70%", background: "#f0f0f0", borderRadius: "4px" }}></div>
            <div style={{ height: "10px", width: "90%", background: "#f5f5f5", borderRadius: "4px", marginTop: "6px" }}></div>
          </div>
        ))}
      </div>

      {/* Form Skeleton */}
      <div className="mt-4">
        <div style={{ height: "16px", width: "150px", background: "#e0e0e0", borderRadius: "4px", marginBottom: "10px" }}></div>

        <div className="d-flex gap-3">
          <div style={{ height: "35px", width: "120px", background: "#f0f0f0", borderRadius: "4px" }}></div>
          <div style={{ height: "35px", width: "100px", background: "#f0f0f0", borderRadius: "4px" }}></div>
          <div style={{ height: "35px", width: "140px", background: "#f0f0f0", borderRadius: "4px" }}></div>
          <div style={{ height: "35px", width: "200px", background: "#f0f0f0", borderRadius: "4px" }}></div>
        </div>

        <div className="d-flex justify-content-end gap-2 mt-4">
          <div style={{ height: "35px", width: "80px", background: "#e0e0e0", borderRadius: "4px" }}></div>
          <div style={{ height: "35px", width: "100px", background: "#d6d6d6", borderRadius: "4px" }}></div>
        </div>
      </div>
    </div>
  </Col>
}
const miniLabelStyle = {
  fontSize: "12px",
  fontWeight: "400",
  color: "#94a3b8",
  display: "block",
  marginBottom: "2px"
};